import React, { Fragment, useCallback, useRef, useState } from 'react';
import Regions from './regions/component';
import Characteristics from './characteristics/component';
import Demographics from './demographics/component';
import ChartContext from '../chart-line-2/ChartContext';
import exportElementAsImage from '../../utils/exportElementAsImage';
import { getAbbreviations } from './abbreviations';
import { object } from 'prop-types';

const UpdatedDataExplorer = ( props ) => {

    const [ region, setRegion ] = useState( [] );
    const [ demographic, setDemographic ] = useState( [] );
    const [ characteristic, setCharacteristic ] = useState( [] );
    const [ nextChartData, setNextChartData ] = useState( [] );
    const [ currentChartData, setCurrentChartData ] = useState( [] );
    const [ disabledDemographics, setDisabledDemographics ] = useState( [] );

    const colors = props.attributes.colors;
    const disclaimer = props.attributes.disclaimer;
    const excludeCharacteristics = props.attributes.excludeCharacteristics;
    const chartRef = useRef();
    const pngExportRef = useRef();
    const pngExportTriggerRef = useRef();

    const parseCharacteristics = ( data ) => {

        const result = [];

        data.forEach( ( point ) => {

            if ( point.hasOwnProperty( 'facets' ) ) {

                point.facets.forEach( ( facet ) => {

                    if ( !result.includes( facet.property ) ) {

                        result.push( facet.property );

                    }

                } );

            }
            else {

                Object.keys( point ).forEach( ( key ) => {

                    if ( !excludeCharacteristics.includes( key ) && !result.includes( key ) ) {

                        result.push( key );

                    }

                } );

            }

        } );

        return result;

    };

    const parseDemographics = ( data ) => {

        const result = [];


        data.forEach( ( point ) => {

            const pointData = point.data;

            const pointKeys = Object.keys( pointData );

            pointKeys.forEach( ( pointKey ) => {


                pointData[pointKey].forEach( ( facet ) => {


                    const obj = { bin: facet.bin, class: facet.class };

                    if ( !result.some( ( el ) => el.bin === facet.bin && el.class === facet.class ) ) {

                        result.push( obj );

                    }

                    if ( facet.hasOwnProperty( 'sub-demographics' ) ) {

                        result.push( ...facet['sub-demographics'].map( ( el ) => {

                            return { bin: el.bin, class: el.class };

                        } ) );

                    }

                } );

            } );

        } );

        return result;

    };

    const characteristicKeys = parseCharacteristics( props.attributes.data.debt_characteristics );
    const demographicKeys = parseDemographics( props.attributes.data.demographics );

    const getRegionData = useCallback( async ( data ) => {

        // update region state
        setRegion( data );

        if ( data.length && characteristic.length ) {

            // we are setting the region to something other
            // than [], AND there is existing characteristic data
            // which means we should update the chart data
            await fetchChartDataByRegion( data, characteristic, demographic );

        }

    } );

    const getCharacteristicData = useCallback( async ( data ) => {

        setCharacteristic( data );

        if ( region.length && data.length ) {

            // we are setting the characteristic state to something other
            // than [], so update the chart data
            await fetchChartDataByRegion( region, data, demographic );

        }

    } );

    const getDemographicData = useCallback( ( data ) => {

        setDemographic( data );

        if ( region.length && characteristic.length && data.length ) {

            fetchChartDataByRegion( region, characteristic, data );

        }

    } );

    const getQuarters = ( demographics, data ) => {

        const quarters = new Set();

        for ( const regionData of data ) {

            for ( const demo of demographics ) {

                const demoKey = `${ demo['bin'] }-${ demo['class'] }`;

                for ( const da of regionData[demoKey] ) {

                    quarters.add( da['qtr'] );

                }

            }

        }

        return quarters;

    };

    const getDemographicLabels = () => {

        const labels = [];

        for ( const key of demographic ) {

            for ( const demographic of props.attributes.data.demographics ) {

                const demographicData = demographic.data;

                const demographicKeys = Object.keys( demographicData );

                for ( const demoKey of demographicKeys ) {

                    const d = demographicData[demoKey];

                    const found = d.find( ( demo ) => key.bin === demo['bin'] && key.class === demo['class'] );

                    if ( found ) {

                        labels.push( found );
                        break;

                    }
                    else {

                        // check for sub-demographics
                        for ( const dGroup of d ) {

                            if ( dGroup.hasOwnProperty( 'sub-demographics' ) ) {

                                const found = dGroup['sub-demographics'].find( ( demo ) => key.bin === demo['bin'] && key.class === demo['class'] );

                                if ( found ) {

                                    labels.push( found );
                                    break;

                                }

                            }

                        }

                    }

                }

            }

        }

        for ( let i = 0; i < labels.length; i++ ) {

            labels[i] = [
                labels[i],
                region.map( ( r ) => {

                    return r['label'];

                } )
            ];

        }

        return labels;

    };

    const getCharacteristicLabels = () => {

        const labels = [];

        for ( const key of characteristic ) {

            const firstLevelKey = key.split( '_' )[0];

            const group = props.attributes.data.debt_characteristics.find( ( i ) => {

                if ( i['prefix'] === firstLevelKey ) {

                    return i;

                }
                else if ( i.hasOwnProperty( 'specialFacetPrefixes' ) && i['specialFacetPrefixes'].includes( key ) ) {

                    return i;

                }

            } );

            const found = group['facets'].find( ( c ) => c['property'] === key );

            if ( found ) labels.push( found );

        }

        return labels;

    };

    const fetchChartDataByRegion = async ( regions, characteristics, demographics ) => {

        const relevantRegionData = [];

        // grab bulk data for each region
        for ( const reg of regions ) {

            // pull from remote api
            const response = await fetch( props.attributes.data.regionalDataEndpoint + reg['value'] );
            const mockData = await response.json();

            for ( let i = 0; i < mockData.length; i++ ) {

                /**
                 * condense each data point down to just the characteristics, demographics, and quarter
                 * e.g. [ ..., { bin: 'all', class: 'all', qtr: '2015-06-01', totdebt_avg: 14354.8 }, ... ]
                 */
                const dataPoint = mockData[i];

                mockData[i] = {
                    bin: dataPoint['bin'],
                    class: dataPoint['class'],
                    qtr: dataPoint['qtr']
                };

                for ( const char of characteristics ) {

                    mockData[i][char] = dataPoint[char];

                }

            }

            relevantRegionData.push( { region: reg['label'], data: mockData } );

        }

        // based on the current state, what demographics should be disabled on the form
        getDisabledDemographics( relevantRegionData );

        if ( demographics.length ) {

            // if we also have demographic data, let's extract all the data we need now
            extractRelevantData( characteristics, demographics, relevantRegionData );

        }

    };

    const orderDataByDate = ( formattedDistilledData ) => {

        return formattedDistilledData.map( ( distilledData ) =>
            distilledData.data.sort( ( d1, d2 ) => {

                if ( d1.length === 2 ) {

                    return new Date( d1[0][1] ).getTime() - new Date( d2[0][1] ).getTime();

                }
                else {

                    return new Date( d1[1] ).getTime() - new Date( d2[1] ).getTime();

                }

            } ) );

    };

    const extractRelevantData = ( characteristics, demographics, bulkData ) => {

        // distills the data collected by region down to data that matches
        // region + characteristics + demographics

        const chartData = bulkData.slice();

        let colorIdx = 0;

        const result = chartData.map( ( d ) => {

            const obj = { region: d['region'], colors: {} };

            for ( const demo of demographics ) {

                const demoKey = `${ demo['bin'] }-${ demo['class'] }`;

                const relevantData = d['data'].filter( ( da ) => da['bin'] === demo['bin'] && da['class'] === demo['class'] ).map( ( r ) => {

                    const distilledData = {};

                    for ( const key in r ) {

                        if ( key === 'qtr' || characteristicKeys.includes( key ) ) distilledData[key] = r[key];

                    }

                    return distilledData;

                } );

                obj[demoKey] = relevantData;
                obj['colors'][demoKey] = colors[colorIdx];
                colorIdx++;

            }

            return obj;

        } );

        const formattedDistilledData = formatDistilledChartData( characteristics, demographics, result );

        // Sort the data based on the date in ASC order
        orderDataByDate( formattedDistilledData );

        setNextChartData( formattedDistilledData );

    };

    const formatDistilledChartData = ( characteristics, demographics, distilledData ) => {

        const quarters = getQuarters( demographics, distilledData.slice() );

        const formattedChartData = [];

        for ( const char of characteristics ) {

            const charData = { char: char, colors: {}, data: [] };

            for ( const data of distilledData ) {

                const regionalPoints = [];

                for ( const demo of demographics ) {

                    const demoKey = `${ demo['bin'] }-${ demo['class'] }`;

                    const demoData = data[demoKey];

                    if ( regionalPoints[0] && regionalPoints[0].length ) {

                        regionalPoints[0].push( demoKey );

                        for ( const date of quarters ) {

                            const dPoint = demoData.find( ( d ) => d['qtr'] === date );

                            const existingPoint = regionalPoints.find( ( r ) => r.includes( date ) );

                            existingPoint.push( dPoint ? dPoint[char] : undefined );

                        }

                    }
                    else {

                        regionalPoints.push( [ 'characteristic-region', 'qtr', demoKey ] );

                        for ( const date of quarters ) {

                            const dPoint = demoData.find( ( d ) => d['qtr'] === date );

                            const point = [ `${ char }&${ data['region'] }`, date, ( dPoint ? dPoint[char] : undefined ) ];

                            regionalPoints.push( point );

                        }

                    }

                }

                charData['data'].push( regionalPoints );

                charData['colors'][data['region']] = data['colors'];

            }

            if ( charData['data'].length === 2 ) {

                const consolidated = [];

                for ( let i = 0; i < charData['data'][0].length; i++ ) {

                    consolidated[i] = [ charData['data'][0][i], charData['data'][1][i] ];

                }

                consolidated[0] = consolidated[0][0];

                charData['data'] = consolidated;

            }

            formattedChartData.push( charData );

        }

        for ( const dataset of formattedChartData ) {

            if ( dataset['data'].length === 1 ) {

                dataset['data'] = dataset['data'].flat();

            }

        }

        return formattedChartData;

    };

    const buildChartSpecs = () => {

        const returnDataset = [];

        for ( const [ idx, charData ] of Object.entries( nextChartData ) ) {

            let name = '';

            let unsortedLegend = [];
            const legendLabels = getDemographicLabels();
            const label = [];
            const dataset = charData['data'];

            for ( const rowLabel of dataset[0].slice( 2 ) ) {

                const match = legendLabels.find( ( l ) => `${ l[0]['bin'] }-${ l[0]['class'] }` === rowLabel );

                if ( match ) {

                    unsortedLegend.push( {
                        title: match[0]['label'],
                        prefix: `${ match[0]['bin'] }-${ match[0]['class'] }`,
                        sub: match[1].map( ( m ) => {

                            return { title: m };

                        } )
                    } );

                }

            }

            unsortedLegend = unsortedLegend.map( ( u ) => {

                for ( const s of u['sub'] ) {

                    s['color'] = charData['colors'][s['title']][u['prefix']];

                }

                return u;

            } );

            for ( const leg of unsortedLegend ) {

                for ( const { title, color } of leg['sub'] ) {

                    const { prefix } = leg;
                    const prefixIdx = dataset[0].indexOf( prefix );

                    let hasData = false;

                    if ( Array.isArray( dataset[1][0] ) ) {

                        for ( const d of dataset.slice( 1 ) ) {

                            const relevantSet = d.find( ( a ) => a[0].includes( title ) );

                            if ( relevantSet[prefixIdx] ) hasData = true;

                        }

                    }
                    else {

                        for ( const d of dataset.slice( 1 ) ) {

                            if ( d[prefixIdx] ) hasData = true;

                        }

                    }

                    leg['sub'].find( ( s ) => s['title'] === title )['hasData'] = hasData;

                    const lastSpce = title.trim().lastIndexOf( ' ' );
                    const reg = title.trim().substring( 0, lastSpce );
                    const cls = title.trim().substring( lastSpce + 1, title.trim().length );

                    if ( cls === 'State' ) {

                        label.push( [ getAbbreviations( reg.trim().replaceAll( ' ', '' )
                            .toLowerCase() ), color ] );

                    }
                    else if ( cls === 'Non-MSA' ) {

                        label.push( [ `${ reg } ${ cls }`, color ] );

                    }
                    else {

                        label.push( [ `${ reg.length > 16 ? `${ reg.slice( 0, 16 ) }...` : reg } ${ cls }`, color ] );

                    }

                }

            }

            const charLabels = getCharacteristicLabels();

            name = charLabels[idx] ? charLabels[idx]['chartTitle'] : undefined;

            const outputData = [ {
                description: {
                    name: name,
                    label: label,
                    legend: unsortedLegend,
                    colors: charData['colors']
                },
                state: {
                    region: region,
                    characteristic: characteristic,
                    demographic: demographic
                },
                data: dataset
            } ];

            const yAxisTitle = charData['char'].split( '_' )[1] === 'avg' ? [ 'Dollars', 'In thousands' ] : [ 'Percent' ];

            const multipleCharts = nextChartData.length > 1;

            const chartFormatData = {
                margin: {
                    top: 45,
                    right: 10,
                    bottom: 50,
                    left: 40
                },
                multipleCharts: multipleCharts,
                height: 370,
                lastUpdated: '17 Jun ’22',
                yAxisTitle: yAxisTitle,
                chartColors: colors,
                disclaimer: disclaimer
            };

            returnDataset.push( [ chartFormatData, outputData ] );

        }

        return returnDataset;

    };

    const getDisabledDemographics = ( bulkData ) => {

        // iterate through the currently set chart data and find if there are any missing demographics
        // missing demographics will be grayed out in the demographics filter
        const foundDemographics = new Set();

        for ( const regionData of bulkData ) {

            for ( const data of regionData['data'] ) {

                const characteristicValues = Object.values( data ).slice( 3 );

                if ( characteristicValues.some( ( c ) => c !== undefined ) ) {

                    // if there are ANY values for characteristics, then we should enable the demographic
                    // if only undefined values are present, there's no relevant data for that demo, so disable it
                    foundDemographics.add( `${ data['bin'] }-${ data['class'] }` );

                }

            }

        }

        setDisabledDemographics( demographicKeys.filter( ( d ) => !foundDemographics.has( `${ d['bin'] }-${ d['class'] }` ) ) );

    };

    const generateNewChart = () => {

        if ( nextChartData.length ) {

            const newChartData = buildChartSpecs();

            setNextChartData( [] );
            setCurrentChartData( newChartData );

        }

    };

    const renderCharts = () => {

        return (
            <ChartContext
                key={ `f${ Math.random() }` }
                componentWrapper={ chartRef.current }
                bulkData = { currentChartData }
                chartColors = { colors }
            />
        );

    };

    const saveToPNG = async () => {

        await exportElementAsImage( pngExportRef.current, pngExportTriggerRef.current );

    };

    const clearChart = useCallback( () => {

        setCurrentChartData( [] );

    } );

    return (
        <Fragment>

            <Regions filters={ props.attributes.data.regions } emit={ getRegionData } clearChart={ clearChart } />

            <div className='ude-cdgroup'>
                <Characteristics clearChart={ clearChart } key={ `char-key-${ !region.length }` } disabled={ !region.length } metadata={ props.attributes.data.debt_characteristics } emit={ getCharacteristicData } />
                <Demographics clearChart={ clearChart } key={ `demo-key-${ !region.length || !characteristic.length }` } disabled={ !region.length || !characteristic.length } demographics={ props.attributes.data.demographics } emit={ getDemographicData } disabledDemographics={ disabledDemographics } />
            </div>

            { !nextChartData.length && currentChartData.length
                ? <p className='ude-generatebtn-other-combination'>Select another combination to generate a new chart.</p>
                : null
            }

            <a onClick={ () => generateNewChart() } className={ `ude-generatebtn ${ ( !region.length || !characteristic.length || !demographic.length ) || !nextChartData.length ? 'ude-generatebtn-disabled' : '' }` }>
                Generate Charts
            </a>

            <div ref={ pngExportRef } className={ `main-chart-div ${ currentChartData.length && 'main-chart-div-border' }` }>
                { currentChartData.length
                    ? <a ref={ pngExportTriggerRef } onClick={ () => saveToPNG() } className='main-chart-download'>
                        <p>PNG</p>

                        <div>
                            <img src='/assets/images/svg/icon-utility/download2.svg' alt='Download' />
                        </div>
                    </a>
                    : ''
                }

                <div ref={ chartRef } className='chartselectionwrapper'>
                    { renderCharts() }
                </div>

                { currentChartData.length
                    ? <div className='chartselectiondisclaimer'>
                        <p>{ disclaimer.notes }</p>
                        <p>{ disclaimer.sources }</p>
                    </div>
                    : ''
                }
            </div>

        </Fragment>
    );

};

UpdatedDataExplorer.propTypes = {
    attributes: object
};

export default UpdatedDataExplorer;